<template>
  <v-card
    v-if="availableMethods.length"
    class="shipping-quick-select-card"
    elevation="2"
    rounded="lg"
  >
    <v-card-text class="quick-select-content">
      <div class="text-subtitle-2 font-weight-medium mb-3">
        <v-icon color="primary" class="mr-1" small>mdi-flash</v-icon>
        Available Methods ({{ availableMethods.length }})
      </div>
      
      <v-row>
        <v-col 
          v-for="(method, index) in availableMethods" 
          :key="`method-${index}`"
          cols="12"
          md="6"
          lg="4"
        >
          <v-card
            outlined
            rounded="lg"
            class="method-card"
            :class="{ 'method-card--selected': selectedMethod === method }"
            @click="selectMethod(method)"
            hover
          >
            <v-card-text class="pa-3">
              <div class="d-flex align-center mb-2">
                <v-icon 
                  :color="selectedMethod === method ? 'primary' : 'grey'" 
                  class="mr-2"
                  small
                >
                  {{ selectedMethod === method ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                </v-icon>
                <div class="method-title text-subtitle-1 font-weight-medium">
                  {{ method.title }}
                </div>
              </div>
              
              <div class="method-details">
                <div class="detail-item">
                  <span class="detail-text">{{ method.carrier_description || method.carrier_name }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-text">{{ method.service_description }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-text">{{ method.label.description }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <div class="text-center mt-4">
        <v-btn
          color="primary"
          rounded
          elevation="1"
          class="px-6"
          :disabled="!selectedMethod"
          @click="applyMethod"
        >
          <v-icon left>mdi-check-circle</v-icon>
          Apply Selected Method
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ShippingQuickSelect',
  props: {
    order: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      selectedMethod: null,
    }
  },
  computed: {
    ...mapState({
      company: state => state.warehouse.company,
    }),
    store() {
      return this.company.data_sources.find(ds => ds.id === this.order.sf_data_source_id)
    },
    availableMethods() {
      if (!this.store || !this.store.shipping_map) return []
      
      const countryCode = this.order.shipping_address.country_code
      const parcelWeight = this.order.total_weight_in_grams || 0
      
      return this.store.shipping_map.filter(method => {
        // Check if method supports the destination country
        const supportsCountry = method.countries && method.countries.includes(countryCode)
        
        // Check if parcel weight is within the method's weight limits
        const withinWeightLimit = !method.max_weight_in_grams || parcelWeight <= method.max_weight_in_grams
        
        return supportsCountry && withinWeightLimit
      })
    }
  },
  methods: {
    selectMethod(method) {
      this.selectedMethod = method
    },
    applyMethod() {
      if (!this.selectedMethod) return
      
      // Emit the same structure as ShippingMapCard
      const methodData = {
        title: this.selectedMethod.title,
        sf_carrier_account_id: this.selectedMethod.sf_carrier_account_id,
        carrier_id: this.selectedMethod.carrier_id,
        carrier_description: this.selectedMethod.carrier_description,
        carrier_name: this.selectedMethod.carrier_name,
        service_id: this.selectedMethod.service_id,
        service_description: this.selectedMethod.service_description,
        billing_number: this.selectedMethod.billing_number || '',
        is_premium: this.selectedMethod.is_premium || false,
        postal_delivery_duty_paid: this.selectedMethod.postal_delivery_duty_paid || false,
        addons: this.selectedMethod.addons || [],
        countries: this.selectedMethod.countries || [],
        paymentMethodType: this.selectedMethod.paymentMethodType || 'INVO',
        min_weight_in_grams: this.selectedMethod.min_weight_in_grams || 0,
        max_weight_in_grams: this.selectedMethod.max_weight_in_grams || 2000,
        label: {
          type: this.selectedMethod.label.type,
          description: this.selectedMethod.label.description,
        },
      }
      
      this.$emit('save', methodData)
      
      // Reset selection
      this.selectedMethod = null
    }
  }
}
</script>

<style lang="scss" scoped>
.shipping-quick-select-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  margin-bottom: 16px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.08) !important;
  }
}

.card-title {
  background-color: rgba(var(--v-primary-base-rgb), 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.quick-select-content {
  animation: fadeIn 0.4s ease-out;
}

.method-card {
  transition: all 0.2s ease;
  cursor: pointer;
  border: 2px solid rgba(0, 0, 0, 0.12);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    border-color: rgba(0, 0, 0, 0.2);
  }
  
  &--selected {
    border-color: var(--v-primary-base);
    background-color: rgba(var(--v-primary-base-rgb), 0.05);
    
    &:hover {
      background-color: rgba(var(--v-primary-base-rgb), 0.08);
      border-color: var(--v-primary-base);
    }
  }
}

.method-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.method-details {
  .detail-item {
    margin-bottom: 2px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .detail-text {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile-specific styles */
@media (max-width: 600px) {
  .method-details {
    .detail-row {
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: 8px;
    }
    
    .detail-label {
      margin-bottom: 2px;
      min-width: auto;
    }
  }
}
</style>
